import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  LabelList,
  Tooltip,
} from "recharts";
import colors from "../../../../theme/Colours";

import {
  SalesAnalyticsCardLabel,
  SalesAnalyticsContentBox,
  StyledSalesAnalyticsCard,
  ProductWodCurrentItemBox,
} from "../styles";

interface WodData {
  name: string;
  details: {
    period: string;
    name: string;
    values: {
      count: string;
    }[];
  }[];
}

interface BarDataEntry {
  name: string;
  percentage: number;
  displayValue: string;
  period: string;
  color: string;
}

export default function WodByClient({
  data,
  periodValues,
}: {
  data: WodData;
  periodValues?: { [key: string]: string };
}) {
  const barData = useMemo(() => {
    if (data?.details && Array.isArray(data.details)) {
      const filteredDetails = data.details.filter(
        (detail) => detail.values && detail.values.length > 0
      );

      const getColorByPeriod = (period: string) => {
        switch (period) {
          case "one":
            return colors.salesAnalyticsProductWodMedium;
          case "two":
            return colors.linePlotOrange;
          default:
            return colors.salesAnalyticsProductWodMedium;
        }
      };
      const periodKeyMap: { [key: string]: string } = {
        one: "Period 1",
        two: "Period 2",
      };

      const rawData: BarDataEntry[] = filteredDetails.map((detail) => {
        const periodKey = periodKeyMap[detail.period];
        const label = periodValues?.[periodKey] || detail.name;
        const numericValue =
          parseFloat(detail.values[0]?.count?.replace("%", "") || "0") || 0;
        return {
          name: label,
          percentage: numericValue,
          displayValue: `${numericValue}%`,
          period: detail.period,
          color: getColorByPeriod(detail.period),
        };
      });

      return rawData;
    } else {
      return [];
    }
  }, [data]);

  const maxBarSize = barData.length === 1 ? 30 : 50;

  if (!barData || barData.length === 0) return <></>;

  return (
    <StyledSalesAnalyticsCard>
      <SalesAnalyticsCardLabel>
        {data?.name || "Width of Distribution (WOD) by Client"}
      </SalesAnalyticsCardLabel>
      <SalesAnalyticsContentBox>
        <ProductWodCurrentItemBox>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={barData}
              layout="vertical"
              margin={{
                top: 30,
                right: 60,
                left: 20,
                bottom: 0,
              }}
            >
              <XAxis
                type="number"
                tick={false}
                axisLine={true}
                label={{
                  position: "insideBottom",
                  offset: 0,
                  style: { textAnchor: "middle", fill: colors.searchInputText },
                }}
              />
              <YAxis
                dataKey="name"
                type="category"
                tick={{
                  fill: colors.searchInputText,
                  fontWeight: "500",
                }}
                width={115}
                axisLine={{
                  stroke: colors.searchInputText,
                  strokeDasharray: "3 3",
                }}
              />
              <Tooltip
                formatter={(value: number) => [`${value}%`]}
                labelStyle={{ color: colors.searchInputText }}
                contentStyle={{
                  backgroundColor: "white",
                  border: `1px solid ${colors.greyBorder}`,
                  borderRadius: "4px",
                }}
                cursor={false}
              />
              <Bar
                dataKey="percentage"
                radius={[4, 4, 0, 0]}
                maxBarSize={maxBarSize}
              >
                {barData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
                <LabelList
                  dataKey="displayValue"
                  position="right"
                  style={{
                    fill: colors.searchInputText,
                    fontSize: "12px",
                    fontWeight: "500",
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ProductWodCurrentItemBox>
      </SalesAnalyticsContentBox>
    </StyledSalesAnalyticsCard>
  );
}
